/**
 * ForgotPassword Component
 *
 * Password-reset form that sends a Firebase password-reset email. The user
 * enters their email address and, on success, sees a confirmation message
 * instructing them to check their inbox (and spam folder). Handles common
 * Firebase error codes such as "auth/user-not-found" with user-friendly
 * messages.
 *
 * Route: /forgot-password (AuthRestrictedRoute, only for non-authenticated users)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Card,
} from 'react-bootstrap';

import SEO from '@/components/common/SEO';
import { useAuth } from '@/shared/contexts/AuthContext';
import './ForgotPassword.css';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const { resetPassword } = useAuth();

    // Submit the email to Firebase's password-reset flow
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');
        setLoading(true);

        try {
            await resetPassword(email);
            setMessage(
                "Check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder."
            );
        } catch (err) {
            if (err.code === 'auth/user-not-found') {
                setError('No account found with this email address');
            } else {
                setError('Failed to send reset email. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Reset Password"
                description="Reset your RILE Connect account password."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Reset Password', path: '/forgot-password' },
                ]}
            />
            <Container className="forgot-password-container py-5">
                <Row className="justify-content-center">
                    <Col md={6} lg={5}>
                        <Card className="shadow-sm">
                            <Card.Body className="p-4">
                                <h2 className="text-center text-primary mb-4">
                                    Reset Password
                                </h2>

                                <p className="text-center text-muted mb-4">
                                    Enter your email address and we&apos;ll send
                                    you a link to reset your password.
                                </p>

                                {/* Feedback alerts: error or success */}
                                {error && (
                                    <Alert variant="danger">{error}</Alert>
                                )}
                                {message && (
                                    <Alert variant="success">{message}</Alert>
                                )}

                                {/* Reset form: single email field */}
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="Enter your email"
                                            required
                                        />
                                    </Form.Group>

                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="w-100 rounded-pill"
                                        disabled={loading}
                                    >
                                        {loading
                                            ? 'Sending...'
                                            : 'Send Reset Link'}
                                    </Button>
                                </Form>

                                {/* Navigation back to login */}
                                <div className="text-center mt-4">
                                    <Link
                                        to="/login"
                                        className="text-primary fw-medium"
                                    >
                                        Back to Login
                                    </Link>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}
